import { Injectable, OnDestroy } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { OnboardingHubEvent } from '../enum/enums';
import { AppStepReviewedByRole, AppStepReviewStatus, StepReviewState } from '../interfaces/onboarding-notification.interfaces';
import {
  mapNotificationToStepUpdates,
  applyReviewedByRoleUpdates,
  applyStepUpdates,
} from '../interfaces/onboarding-notification.mapper';
import { environment } from '../../../environments/environment';

export interface OnboardingNotification {
  applicationId: string;
  status: string;
  message: string;
  timestamp: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly hubUrl = `${environment.baseUrl}/lms/hubs/onboarding`;

  private hubConnection: HubConnection | null = null;

  private readonly _connectionState$ = new BehaviorSubject<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  public readonly connectionState$: Observable<HubConnectionState> =
    this._connectionState$.asObservable();

  private readonly _notification$ = new Subject<OnboardingNotification>();
  public readonly notification$: Observable<OnboardingNotification> =
    this._notification$.asObservable();

  private readonly _rawNotification$ = new Subject<unknown>();
  public readonly rawNotification$: Observable<unknown> =
    this._rawNotification$.asObservable();

  private readonly _stepReviewStatus$ =
    new BehaviorSubject<AppStepReviewStatus>({});
  public readonly stepReviewStatus$: Observable<AppStepReviewStatus> =
    this._stepReviewStatus$.asObservable();

  private readonly _stepReviewedByRole$ =
    new BehaviorSubject<AppStepReviewedByRole>({});
  public readonly stepReviewedByRole$: Observable<AppStepReviewedByRole> =
    this._stepReviewedByRole$.asObservable();

  private readonly _error$ = new Subject<string>();
  public readonly error$: Observable<string> = this._error$.asObservable();

  async startConnection(tokenFactory: () => string): Promise<void> {
    if (
      this.hubConnection &&
      this.hubConnection.state !== HubConnectionState.Disconnected
    ) {
      console.warn('[SignalR] Connection already active — skipping.');
      return;
    }

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => tokenFactory(),
        transport:
          HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          const delays = [0, 2000, 5000, 10000, 30000];
          return retryContext.previousRetryCount < delays.length
            ? delays[retryContext.previousRetryCount]
            : 60000;
        },
      })
      .configureLogging(LogLevel.Information)
      .build();

    this.registerLifecycleCallbacks();
    this.registerHubListeners();

    try {
      await this.hubConnection.start();
      console.log('[SignalR] Connected via', this.hubConnection.connectionId);
      this._connectionState$.next(this.hubConnection.state);
    } catch (err) {
      console.error('[SignalR] Connection failed:', err);
      this._connectionState$.next(HubConnectionState.Disconnected);
      this._error$.next(
        err instanceof Error ? err.message : 'Connection failed'
      );
    }
  }

  async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this._connectionState$.next(HubConnectionState.Disconnected);
      console.log('[SignalR] Disconnected.');
    }
  }

  ngOnDestroy(): void {
    this.stopConnection();
    this._notification$.complete();
    this._rawNotification$.complete();
    this._stepReviewStatus$.complete();
    this._stepReviewedByRole$.complete();
    this._connectionState$.complete();
    this._error$.complete();
  }

  private registerHubListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on(
      'ReceiveOnboardingUpdate',
      (data: unknown) => {
        console.log('[SignalR] Received:', data);
        this._notification$.next(data as OnboardingNotification);
        this._rawNotification$.next(data);
        this.accumulateReviewState(data);
      }
    );

    const hubEvents: OnboardingHubEvent[] = [
      OnboardingHubEvent.ApplicationCreated,
      OnboardingHubEvent.WorkflowForwardedToCompliance,
      OnboardingHubEvent.WorkflowEscalatedToCeo,
      OnboardingHubEvent.WorkflowApproved,
      OnboardingHubEvent.WorkflowRejected,
      OnboardingHubEvent.WorkflowReturnedToInvestor,
      OnboardingHubEvent.WorkflowReturnedToRM,
      OnboardingHubEvent.WorkflowSentToSigning,
      OnboardingHubEvent.EntityRejected,
      OnboardingHubEvent.EntityVerified,
      OnboardingHubEvent.BulkEntitiesRejected,
      OnboardingHubEvent.BulkEntitiesVerified,
    ];

    for (const event of hubEvents) {
      this.hubConnection.on(event, (data: unknown) => {
        console.log(`[SignalR] ${event}:`, data);
        this._notification$.next(data as OnboardingNotification);
        this._rawNotification$.next(data);
        this.accumulateReviewState(data);
      });
    }
  }

  private accumulateReviewState(raw: unknown): void {
    const updates = mapNotificationToStepUpdates(raw);
    if (updates.length === 0) return;
    const next = applyStepUpdates(this._stepReviewStatus$.value, updates);
    this._stepReviewStatus$.next(next);
    const nextReviewedByRole = applyReviewedByRoleUpdates(this._stepReviewedByRole$.value, updates);
    this._stepReviewedByRole$.next(nextReviewedByRole);
  }

  clearStepReviewStatus(): void {
    this._stepReviewStatus$.next({});
    this._stepReviewedByRole$.next({});
  }

  setInitialStepReviewStatus(
    applicationId: number,
    statusByRoute: Record<string, StepReviewState>,
    reviewedByRoleByRoute: Record<string, number> = {},
  ): void {
    if (!Number.isInteger(applicationId) || applicationId <= 0) return;
    const current = this._stepReviewStatus$.value;
    const next: Record<number, Record<string, StepReviewState>> = {
      ...current,
      [applicationId]: { ...statusByRoute },
    };
    this._stepReviewStatus$.next(next);

    const currentReviewedByRole = this._stepReviewedByRole$.value;
    const nextReviewedByRole: Record<number, Record<string, number>> = {
      ...currentReviewedByRole,
      [applicationId]: { ...reviewedByRoleByRoute },
    };
    this._stepReviewedByRole$.next(nextReviewedByRole);
  }

  private registerLifecycleCallbacks(): void {
    if (!this.hubConnection) return;

    this.hubConnection.onreconnecting((error) => {
      console.warn('[SignalR] Reconnecting...', error?.message);
      this._connectionState$.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected:', connectionId);
      this._connectionState$.next(HubConnectionState.Connected);
    });

    this.hubConnection.onclose((error) => {
      console.warn('[SignalR] Connection closed.', error?.message);
      this._connectionState$.next(HubConnectionState.Disconnected);
      if (error) {
        this._error$.next(error.message);
      }
    });
  }
}
